import { useEffect, useCallback } from 'react';
import {
  MapContainer, TileLayer, Marker, Popup, Circle, Polygon, Tooltip, AttributionControl, useMap, useMapEvents
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  vehicleZeichenSpec, symbolZeichenSpec, renderZeichen, scaledSize,
  isArrowSymbol, arrowSvg, sichtungSvg
} from '../lib/taktischeZeichen';
import { HydrantLayer } from './HydrantLayer';
import { PoiLayer } from './PoiLayer';
import { LineElement } from './LineElement';
import { resolveLineStyle, dashArray, visibleLabel } from '../lib/lineStyles';
import { BASE_MAPS, DEFAULT_BASE_MAP } from '../lib/baseMaps';
import { DrawingLayer } from './DrawingLayer';
import { vehicleDisplayName } from '../lib/fuehrungsDiagramm';

// Über der höchsten Stufe des Kartenstils (maxNativeZoom) werden Kacheln hochskaliert
const MAX_ZOOM = 22;

// Fix for default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Werte landen als HTML in L.divIcon und müssen daher escaped werden
const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Nur Hex-Farben zulassen, da sie in SVG-Attributen landen
const safeColor = (value, fallback = '#ef4444') =>
  /^#[0-9a-fA-F]{3,8}$/.test(value || '') ? value : fallback;

// Drehung eines Markers in Grad (element.rotation)
const rotateStyle = (rotation) => {
  const deg = Number(rotation) || 0;
  return deg ? ` style="transform: rotate(${deg}deg)"` : '';
};

const rotated = (html, rotation) => `<div class="tz-rot"${rotateStyle(rotation)}>${html}</div>`;

// Custom icon creator for vehicles and symbols
const createCustomIcon = (element, isSelected) => {
  const { type, properties } = element;
  const color = safeColor(properties?.color);
  const label = escapeHtml(String(properties?.label || properties?.name || type).substring(0, 4));
  
  const svgIcon = `
    <svg width="40" height="48" viewBox="0 0 40 48" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.3"/>
        </filter>
      </defs>
      <path d="M20 0 C8.954 0 0 8.954 0 20 C0 35 20 48 20 48 C20 48 40 35 40 20 C40 8.954 31.046 0 20 0Z" 
            fill="${color}" filter="url(#shadow)"/>
      <circle cx="20" cy="20" r="12" fill="white" opacity="0.9"/>
      <text x="20" y="24" text-anchor="middle" font-size="8" font-weight="600" fill="${color}">${label}</text>
    </svg>
  `;

  return L.divIcon({
    html: `<div class="tz-marker${isSelected ? ' tz-selected' : ''}">${rotated(svgIcon, element.rotation)}</div>`,
    className: 'custom-marker',
    iconSize: [40, 48],
    iconAnchor: [20, 48],
    popupAnchor: [0, -48]
  });
};

// Höhe der taktischen Zeichen auf der Karte in Pixeln
const ZEICHEN_HEIGHT = 38;

// Taktisches Zeichen als Marker, optional mit Beschriftung darunter
// Gedreht wird nur das Zeichen, die Beschriftung bleibt waagerecht.
// label: Text oder [Hauptzeile, Nebenzeile]
const createZeichenIcon = (zeichen, label, isSelected, rotation) => {
  const [width, height] = scaledSize(zeichen.size, ZEICHEN_HEIGHT);
  const [main, sub] = Array.isArray(label) ? label : [label];
  const caption = main
    ? `<span class="tz-label">${escapeHtml(main)}${sub ? `<span class="tz-label-sub">${escapeHtml(sub)}</span>` : ''}</span>`
    : '';
  return L.divIcon({
    html: `<div class="tz-marker${isSelected ? ' tz-selected' : ''}"><img src="${zeichen.url}" width="${width}" height="${height}" alt="" draggable="false"${rotateStyle(rotation)}/>${caption}</div>`,
    className: 'tz-marker-wrap',
    iconSize: [width, height],
    iconAnchor: [width / 2, height / 2],
    popupAnchor: [0, -height / 2]
  });
};

// Vehicle icon creator
const createVehicleIcon = (vehicle, isSelected, rotation) => {
  const zeichen = renderZeichen(vehicleZeichenSpec(vehicle));
  const displayName = vehicleDisplayName(vehicle);
  const label = vehicle.callsign ? [vehicle.callsign, displayName] : displayName;
  if (zeichen) return createZeichenIcon(zeichen, label, isSelected, rotation);

  const color = safeColor(vehicle.color);
  const fallbackLabel = escapeHtml(String(displayName || vehicle.type || 'FW').substring(0, 8));
  
  const svgIcon = `
    <svg width="48" height="32" viewBox="0 0 48 32" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="2" width="44" height="28" rx="4" fill="${color}"/>
      <text x="24" y="20" text-anchor="middle" font-size="9" font-weight="700" fill="white" font-family="Barlow, sans-serif">${fallbackLabel}</text>
    </svg>
  `;

  return L.divIcon({
    html: `<div class="tz-marker${isSelected ? ' tz-selected' : ''}">${rotated(svgIcon, rotation)}</div>`,
    className: 'vehicle-marker',
    iconSize: [48, 32],
    iconAnchor: [24, 16],
    popupAnchor: [0, -16]
  });
};

// Richtungspfeil, drehbar über element.rotation
// Punkt-Pfeile aus älteren Szenarien; Bezeichnung/Funkrufname werden wie bei Zeichen darunter angezeigt
const createArrowIcon = (symbol, rotation, isSelected) => L.divIcon({
  html: `<div class="tz-marker${isSelected ? ' tz-selected' : ''}">${arrowSvg(symbol, rotation)}${
    symbol.callsign || symbol.customName
      ? `<span class="tz-label">${escapeHtml(symbol.callsign || symbol.customName)}</span>`
      : ''
  }</div>`,
  className: 'tz-marker-wrap',
  iconSize: [56, 56],
  iconAnchor: [28, 28],
  popupAnchor: [0, -28]
});

// Symbol icon creator for DV102 symbols
const createSymbolIcon = (symbol, rotation, isSelected) => {
  if (isArrowSymbol(symbol)) return createArrowIcon(symbol, rotation, isSelected);

  const sichtung = sichtungSvg(symbol);
  if (sichtung) {
    return L.divIcon({
      html: `<div class="tz-marker${isSelected ? ' tz-selected' : ''}">${rotated(sichtung, rotation)}</div>`,
      className: 'tz-marker-wrap',
      iconSize: [32, 32],
      iconAnchor: [16, 16],
      popupAnchor: [0, -16]
    });
  }

  const zeichen = renderZeichen(symbolZeichenSpec(symbol));
  if (zeichen) {
    const label = symbol.callsign || symbol.customName
      ? [symbol.callsign || symbol.customName, symbol.callsign ? symbol.customName : '']
      : null;
    return createZeichenIcon(zeichen, label, isSelected, rotation);
  }

  // Fallback für Elemente ohne DV-102-Zeichen (z.B. Zonen)
  const color = safeColor(symbol.color);
  const category = symbol.category || 'gefahr';
  
  // Kategorien entsprechen den DV102_SYMBOLS im Backend
  let shape;
  switch (category) {
    case 'gefahr':
    case 'schaden':
      shape = `<polygon points="20,4 36,32 4,32" fill="none" stroke="${color}" stroke-width="3"/>`;
      break;
    case 'fuehrung':
      shape = `<path d="M8 36 V4 H32 L26 12 L32 20 H8" fill="none" stroke="${color}" stroke-width="3"/>`;
      break;
    case 'raum':
    case 'medizin':
    case 'atemschutz':
    case 'wasser':
    case 'infra':
    case 'komm':
      shape = `<rect x="4" y="4" width="32" height="32" fill="none" stroke="${color}" stroke-width="3" rx="2"/>`;
      break;
    case 'sichtung':
      shape = `<circle cx="20" cy="20" r="16" fill="${color}" opacity="0.8"/>`;
      break;
    case 'zone':
      shape = `<circle cx="20" cy="20" r="16" fill="none" stroke="${color}" stroke-width="3" stroke-dasharray="6,4"/>`;
      break;
    case 'pfeil':
      shape = `<path d="M4 20 H30 M22 12 L32 20 L22 28" fill="none" stroke="${color}" stroke-width="3"/>`;
      break;
    default:
      shape = `<rect x="4" y="4" width="32" height="32" fill="${color}" opacity="0.2" stroke="${color}" stroke-width="2"/>`;
  }

  const svgIcon = `
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      ${shape}
      <text x="20" y="24" text-anchor="middle" font-size="10" font-weight="600" fill="${category === 'sichtung' ? 'white' : color}">${escapeHtml(symbol.name?.substring(0, 2) || '!')}</text>
    </svg>
  `;

  return L.divIcon({
    html: `<div class="tz-marker${isSelected ? ' tz-selected' : ''}">${rotated(svgIcon, rotation)}</div>`,
    className: 'symbol-marker',
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

// Map reference provider
const MapRefProvider = ({ onMapReady }) => {
  const map = useMap();
  
  useEffect(() => {
    if (map && onMapReady) {
      onMapReady(map);
    }
  }, [map, onMapReady]);
  
  return null;
};

// Map click handler component
const MapClickHandler = ({ onMapClick, activeTool }) => {
  useMapEvents({
    click: (e) => {
      if (activeTool && onMapClick) {
        onMapClick(e.latlng, activeTool);
      }
    }
  });
  return null;
};

// Beim Zeichnen/Platzieren würde schnelles Tippen sonst die Karte zoomen
const DoubleClickZoomControl = ({ disabled }) => {
  const map = useMap();

  useEffect(() => {
    if (disabled) map.doubleClickZoom.disable();
    else map.doubleClickZoom.enable();
  }, [map, disabled]);

  return null;
};

// Leaflet wertet nur bis zu 3 Pixel Bewegung als Klick. Mit dem Finger verrutscht ein Tipp
// oft weiter; Safari (iPhone/iPad) meldet jede dieser Bewegungen, Leaflet verwarf den Tipp dann
// als Verschieben der Karte (Punkt/Fahrzeug nicht gesetzt).
// Gilt über die Voreinstellung für alle Draggables, also Karte und Marker.
const TOUCH_CLICK_TOLERANCE = 10;
const MOUSE_CLICK_TOLERANCE = 3;

const TapToleranceControl = ({ touch }) => {
  useEffect(() => {
    L.Draggable.prototype.options.clickTolerance = touch ? TOUCH_CLICK_TOLERANCE : MOUSE_CLICK_TOLERANCE;
  }, [touch]);

  return null;
};

// Leaflet bemerkt nur Änderungen der Fenstergröße. Wechselt das Layout (Gerät gedreht,
// Seitenleiste eingeklappt), muss die Karte ihre Größe neu bestimmen, sonst bleiben graue Flächen.
const MapResizeWatcher = () => {
  const map = useMap();

  useEffect(() => {
    let frame = null;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => map.invalidateSize({ pan: false }));
    });
    observer.observe(map.getContainer());
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [map]);

  return null;
};

// Map center updater
const MapCenterUpdater = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    if (center && center.lat && center.lng) {
      map.setView([center.lat, center.lng], zoom || map.getZoom());
    }
  }, [center, zoom, map]);
  
  return null;
};

export const MapComponent = ({ 
  center = { lat: 51.1657, lng: 10.4515 }, 
  zoom = 6,
  elements = [],
  onMapClick,
  onElementClick,
  onElementDrag,
  activeTool,
  onMapReady,
  mapBrightness = 100,
  isDarkMode = false,
  drawingPoints = [],
  measurePoints = [],
  selectedElement = null,
  showHydrants = false,
  onHydrantStatus,
  onFinishDrawing,
  isTouch = false,
  suppressDoubleClickZoom = false,
  baseMap = DEFAULT_BASE_MAP,
  drawColor,
  poiCategories = [],
  onPoiStatus
}) => {
  const tiles = BASE_MAPS[baseMap] || BASE_MAPS[DEFAULT_BASE_MAP];

  const handleMarkerDrag = useCallback((elementId, newPosition) => {
    if (onElementDrag) {
      onElementDrag(elementId, newPosition);
    }
  }, [onElementDrag]);

  const renderElement = (element) => {
    const { id, type, position, positions, radius, properties } = element;
    const isSelected = selectedElement === id;

    switch (type) {
      case 'vehicle':
        return (
          <Marker
            key={id}
            position={[position.lat, position.lng]}
            icon={createVehicleIcon(properties, isSelected, element.rotation)}
            draggable={!isTouch || isSelected}
            eventHandlers={{
              click: () => onElementClick?.(element),
              dragend: (e) => handleMarkerDrag(id, e.target.getLatLng())
            }}
            opacity={isSelected ? 1 : 0.9}
          >
            <Popup>
              <div className="font-sans">
                <strong className="font-barlow">{properties?.callsign || vehicleDisplayName(properties)}</strong>
                {properties?.callsign && <p className="text-sm">{vehicleDisplayName(properties)}</p>}
                {properties?.description && <p className="text-sm mt-1 text-muted-foreground">{properties.description}</p>}
              </div>
            </Popup>
          </Marker>
        );

      case 'symbol':
        return (
          <Marker
            key={id}
            position={[position.lat, position.lng]}
            icon={createSymbolIcon(properties, element.rotation, isSelected)}
            draggable={!isTouch || isSelected}
            eventHandlers={{
              click: () => onElementClick?.(element),
              dragend: (e) => handleMarkerDrag(id, e.target.getLatLng())
            }}
          >
            <Popup>
              <div className="font-sans">
                <strong className="font-barlow">{properties?.name || 'Symbol'}</strong>
                <p className="text-sm text-muted-foreground capitalize">{properties?.category}</p>
              </div>
            </Popup>
          </Marker>
        );

      case 'marker':
        return (
          <Marker
            key={id}
            position={[position.lat, position.lng]}
            icon={createCustomIcon(element, isSelected)}
            draggable={!isTouch || isSelected}
            eventHandlers={{
              click: () => onElementClick?.(element),
              dragend: (e) => handleMarkerDrag(id, e.target.getLatLng())
            }}
          >
            <Popup>
              <div className="font-sans">
                <strong>{properties?.label || 'Markierung'}</strong>
              </div>
            </Popup>
          </Marker>
        );

      case 'circle': {
        const style = resolveLineStyle(properties);
        const label = visibleLabel(properties?.label);
        return (
          <Circle
            key={id}
            center={[position.lat, position.lng]}
            radius={radius || 100}
            pathOptions={{
              color: style.color,
              fillColor: style.color,
              fillOpacity: 0.2,
              weight: isSelected ? 5 : 2,
              dashArray: properties?.dashed ? '10, 10' : undefined
            }}
            // Klick nicht an die Karte weitergeben, die sonst die Auswahl sofort wieder aufhebt
            bubblingMouseEvents={false}
            eventHandlers={{
              click: () => onElementClick?.(element)
            }}
          >
            {label && <Tooltip permanent direction="center" className="line-label">{label}</Tooltip>}
          </Circle>
        );
      }

      case 'polygon': {
        if (!positions || positions.length < 3) return null;
        const style = resolveLineStyle(properties);
        const label = visibleLabel(properties?.label);
        return (
          <Polygon
            key={id}
            positions={positions.map(p => [p.lat, p.lng])}
            pathOptions={{
              color: style.color,
              fillColor: style.color,
              fillOpacity: 0.2,
              weight: isSelected ? 5 : 2,
              dashArray: dashArray(style.dashed, 2)
            }}
            // Klick nicht an die Karte weitergeben, die sonst die Auswahl sofort wieder aufhebt
            bubblingMouseEvents={false}
            eventHandlers={{
              click: () => onElementClick?.(element)
            }}
          >
            {label && <Tooltip permanent direction="center" className="line-label">{label}</Tooltip>}
          </Polygon>
        );
      }

      case 'polyline':
        return (
          <LineElement
            key={id}
            element={element}
            isSelected={isSelected}
            onClick={onElementClick}
          />
        );

      default:
        return null;
    }
  };

  // Calculate filter style for brightness and dark mode
  const getMapFilterStyle = () => {
    if (isDarkMode) {
      // Dark mode: invert colors and adjust brightness
      return {
        filter: `invert(1) hue-rotate(180deg) brightness(${mapBrightness / 100}) contrast(0.9)`
      };
    } else {
      // Light mode: just brightness
      return {
        filter: mapBrightness !== 100 ? `brightness(${mapBrightness / 100})` : 'none'
      };
    }
  };

  return (
    <MapContainer
      center={[center.lat, center.lng]}
      zoom={zoom}
      maxZoom={MAX_ZOOM}
      style={{ height: '100%', width: '100%' }}
      className={isDarkMode ? 'dark-map' : ''}
      attributionControl={false}
    >
      {/* Ohne „Leaflet“-Präfix, damit die Quellenangabe auf dem Handy in eine Zeile passt */}
      <AttributionControl prefix={false} />
      <TileLayer
        key={baseMap}
        attribution={tiles.attribution}
        url={tiles.url}
        maxNativeZoom={tiles.maxNativeZoom}
        maxZoom={MAX_ZOOM}
        className="map-tiles"
      />
      
      {/* Apply filter to map tiles via style injection */}
      <style>{`
        .dark-map .leaflet-tile-pane {
          filter: ${getMapFilterStyle().filter};
        }
        .dark-map {
          background: #1a1a1a;
        }
        .selected-element {
          filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.8));
        }
      `}</style>
      
      <MapRefProvider onMapReady={onMapReady} />
      <MapClickHandler onMapClick={onMapClick} activeTool={activeTool} />
      <DoubleClickZoomControl disabled={suppressDoubleClickZoom} />
      <MapResizeWatcher />
      <TapToleranceControl touch={isTouch} />
      <MapCenterUpdater center={center} zoom={zoom} />
      <HydrantLayer enabled={showHydrants} onStatusChange={onHydrantStatus} />
      <PoiLayer categories={poiCategories} onStatusChange={onPoiStatus} />
      
      {elements.map(renderElement)}
      
      <DrawingLayer
        activeTool={activeTool}
        drawingPoints={drawingPoints}
        measurePoints={measurePoints}
        cursorPreview={!isTouch}
        drawColor={drawColor}
        onFinishDrawing={onFinishDrawing}
      />
    </MapContainer>
  );
};

export default MapComponent;
